import { FabricEventHandler, type TFabricEngineEvents } from '@/infrastructure/engine2d/FabricEventHandler';
import { TypedEventEmitter } from '@/utils/TypedEventEmitter';
import type { TSelectionRange } from '@/domain/textbox/TSelectionRange';
import type { TViewTransform } from '@/application/ports/TViewTransform';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { Canvas } from 'fabric';

type TFabricPortEventsServiceParams = {
    canvas: Canvas;
    scene: FabricScene;
};

// Owns the port-level event bus and the fabric → typed-emitter
// translation. Distinct from FabricSceneEventsService (which handles
// scene-internal fabric hooks — constraint, contextual menu — with no
// bus). Consumers (Application layer) get subscribed via I2DEngine.on*.
class FabricPortEventsService {
    private readonly _canvas: Canvas;
    private readonly _events = new TypedEventEmitter<TFabricEngineEvents>();
    private readonly _eventHandler: FabricEventHandler;

    constructor(params: TFabricPortEventsServiceParams) {
        this._canvas = params.canvas;
        this._eventHandler = new FabricEventHandler({ canvas: this._canvas, events: this._events });
        params.scene.setZoomNotifier((zoom) => this._events.emit('zoom', zoom));
    }

    dispose(): void {
        this._eventHandler.dispose();
        this._events.removeAllListeners();
    }

    // Programmatic zoom entry (overlay engine owns zoomTo) — reuses
    // the same channel as wheel / pinch.
    dispatchZoomChanged(zoom: number): void {
        this._events.emit('zoom', zoom);
    }

    onAfterRender(callback: () => void): () => void {
        function wrapped() {
            callback();
        }
        this._canvas.on('after:render', wrapped);
        return () => this._canvas.off('after:render', wrapped);
    }

    onSelectionChanged(callback: (entityId: string | undefined) => void): () => void {
        return this._events.subscribe('selection', callback);
    }

    onTextChanged(callback: (payload: { id: string; text: string }) => void): () => void {
        return this._events.subscribe('textChanged', callback);
    }

    onTextSelectionChanged(callback: (range: TSelectionRange | undefined) => void): () => void {
        return this._events.subscribe('textSelection', callback);
    }

    onViewTransformed(callback: (transform: TViewTransform) => void): () => void {
        return this._events.subscribe('transform', callback);
    }

    onZoomChanged(callback: (zoom: number) => void): () => void {
        return this._events.subscribe('zoom', callback);
    }
}

export { FabricPortEventsService };
